<template>
  <div class="bind-phone-container">
    <div class="bind-phone-header">
      <span class="back" @click="$router.push('/settings')">&#8592;</span>
      <span class="title">绑定手机</span>
    </div>
    
    <div v-if="loading" class="loading-container">
      <div class="loading-text">加载中...</div>
    </div>
    
    <div v-else-if="error" class="error-container">
      <div class="error-text">{{ error }}</div>
      <button class="retry-btn" @click="loadUserInfo">重试</button>
    </div>
    
    <div v-else class="bind-phone-content">
      <div v-if="currentPhone" class="current-phone-section">
        <div class="current-phone-label">已绑定手机：</div>
        <div class="current-phone-value">{{ currentPhone }}</div>
      </div>
      
      <div class="form-section">
        <div class="form-group">
          <label class="form-label">新手机号</label>
          <input
            v-model="formData.phone"
            type="tel"
            class="form-input"
            placeholder="请输入新手机号"
            maxlength="11"
            @input="handlePhoneInput"
          />
        </div>
        
        <div v-if="submitError" class="error-message">
          {{ submitError }}
        </div>
        
        <button
          class="submit-btn"
          :class="{ disabled: !isFormValid || submitting }"
          :disabled="!isFormValid || submitting"
          @click="handleSubmit"
        >
          {{ submitting ? '提交中...' : '绑定新手机' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getUserInfo, updatePhone } from '@/api/user'

export default {
  name: 'BindPhonePage',
  data () {
    return {
      loading: true,
      error: null,
      currentPhone: null,
      formData: {
        phone: ''
      },
      submitting: false,
      submitError: null
    }
  },
  computed: {
    isFormValid () {
      const phone = this.formData.phone.trim()
      // 手机号必须是11位数字
      return /^1[3-9]\d{9}$/.test(phone)
    }
  },
  mounted () {
    this.loadUserInfo()
  },
  methods: {
    async loadUserInfo () {
      this.loading = true
      this.error = null
      try {
        const response = await getUserInfo()
        const userData = response?.data || response
        // 获取当前手机号（可能是脱敏的）
        if (userData.phone) {
          this.currentPhone = userData.phone
        }
      } catch (error) {
        console.error('获取用户信息失败:', error)
        if (error?.response?.data) {
          const errorData = error.response.data
          this.error = errorData.message || errorData.msg || '获取用户信息失败，请稍后重试'
        } else {
          this.error = error?.message || '获取用户信息失败，请稍后重试'
        }
      } finally {
        this.loading = false
      }
    },
    handlePhoneInput (e) {
      // 只允许输入数字
      const value = e.target.value.replace(/\D/g, '')
      this.formData.phone = value
      this.submitError = null
    },
    async handleSubmit () {
      if (!this.isFormValid) {
        this.submitError = '请输入正确的手机号'
        return
      }
      
      const phone = this.formData.phone.trim()
      
      // 验证手机号格式
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        this.submitError = '请输入正确的手机号（11位数字，以1开头）'
        return
      }
      
      // 如果当前已绑定手机，检查是否输入的是同一个手机号
      if (this.currentPhone) {
        // 提取当前手机号的数字部分进行比较
        const currentPhoneDigits = this.currentPhone.replace(/\D/g, '')
        if (phone === currentPhoneDigits) {
          this.submitError = '新手机号不能与当前绑定的手机号相同'
          return
        }
      }
      
      this.submitting = true
      this.submitError = null
      
      try {
        await updatePhone({ phone })
        
        // 绑定成功
        alert('手机号绑定成功')
        // 重新加载用户信息
        await this.loadUserInfo()
        // 清空输入框
        this.formData.phone = ''
      } catch (error) {
        console.error('绑定手机号失败:', error)
        // 处理错误信息
        if (error?.response?.data) {
          const errorData = error.response.data
          this.submitError = errorData.message || errorData.msg || '绑定手机号失败，请稍后重试'
        } else {
          this.submitError = error?.message || '绑定手机号失败，请稍后重试'
        }
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.bind-phone-container {
  width: 100vw;
  max-width: 430px;
  min-height: 100vh;
  background: #f7f8fa;
  margin: 0 auto;
  box-sizing: border-box;
}

.bind-phone-header {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-bottom: 1px solid #f2f2f2;
  position: relative;
  font-size: 20px;
  font-weight: bold;
}

.bind-phone-header .back {
  position: absolute;
  left: 0;
  height: 56px;
  display: flex;
  align-items: center;
  font-size: 26px;
  padding-left: 16px;
  cursor: pointer;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.loading-text,
.error-text {
  font-size: 16px;
  color: #888;
  margin-bottom: 20px;
}

.error-text {
  color: #f56c6c;
}

.retry-btn {
  background: #3572e6;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s;
}

.retry-btn:active {
  background: #2a5bc7;
}

.bind-phone-content {
  padding: 24px 16px;
}

.current-phone-section {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px #e0e0e033;
  display: flex;
  align-items: center;
  gap: 8px;
}

.current-phone-label {
  font-size: 15px;
  color: #666;
  font-weight: 500;
}

.current-phone-value {
  font-size: 16px;
  color: #222;
  font-weight: 600;
}

.form-section {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px #e0e0e033;
}

.form-group {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  font-size: 15px;
  color: #222;
  font-weight: 500;
  margin-bottom: 12px;
}

.form-input {
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  color: #222;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #3572e6;
}

.form-input::placeholder {
  color: #999;
}

.error-message {
  margin-top: -16px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 8px;
  color: #f56c6c;
  font-size: 14px;
}

.submit-btn {
  width: 100%;
  height: 48px;
  background: #3572e6;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
  margin-top: 8px;
}

.submit-btn:active:not(.disabled) {
  background: #2a5bc7;
  transform: scale(0.98);
}

.submit-btn.disabled {
  background: #d9d9d9;
  color: #999;
  cursor: not-allowed;
}
</style>

